import React from 'react'

import { useUser } from '../../configuration/hooks/useUser'
import setLocale from '../../locales/dayJsHelper'
import getI18nMap from '../../locales/I18nMap'
import {
  getPreferredLanguage,
  setPreferredLanguage,
} from '../../src/lib/storage'
import { getQueryParamValue } from '../../src/lib/uri'
import { LanguageCodes } from '../constants/enums'

const ALLOW_LISTED_LOCALES: string[] = Object.values(LanguageCodes)

export const translateI18n = (
  i18nMap: Record<string, any>,
  key: string,
  replacementMap: Record<string, string> = {},
  fallback?: string,
) => {
  if (!i18nMap) {
    return
  }

  let theValue = key.split('.').reduce((acc, cv) => acc?.[cv], i18nMap)

  if (theValue) {
    Object.keys(replacementMap).forEach((key) => {
      const replacementValue = replacementMap[key]
      const regex = new RegExp(`%{${key}}`, 'g')
      theValue = theValue!.replace(regex, replacementValue)
    })

    return theValue
  }

  // if this far, value not found

  if (fallback) {
    return fallback
  }

  throw new Error(
    `Language mapping not found for ${key}. Check the locales/<language_code>.ts files for a missing key or value.`,
  )
}

interface I18nProps {
  languageCode: LanguageCodes
  t: (
    key: string,
    replacementMap?: Record<string, any>,
    fallback?: string,
  ) => string
}

export const I18nContext = React.createContext<I18nProps>({
  languageCode: LanguageCodes.ENGLISH,
  t: (
    _key: string,
    _replacementMap?: Record<string, any>,
    _fallback?: string,
  ) => '',
})

export const I18nTranslationProvider: React.FC<
  React.PropsWithChildren<{ localeMap?: any }>
> = ({ children, localeMap }) => {
  const { preferredLanguage } = useUser()
  const [i18nMap, seti18nMap] = React.useState<undefined | Record<string, any>>(
    localeMap,
  )
  const localeQS = (getQueryParamValue('locale') ?? '').toLowerCase()
  let queryStringLang: LanguageCodes | undefined
  if (ALLOW_LISTED_LOCALES.includes(localeQS)) {
    queryStringLang = localeQS as LanguageCodes

    // remove 'locale' from the current URL's search parameters
    const currentURL = new URL(window.location.href)
    currentURL.searchParams.delete('locale')
    window.history.replaceState({}, '', currentURL.href)
  }

  const languageCode =
    queryStringLang || preferredLanguage || getPreferredLanguage()

  const i18nTranslate = React.useCallback(
    (
      key: string,
      replacementMap: Record<string, string> = {},
      fallback?: string,
    ) => {
      return i18nMap
        ? translateI18n(i18nMap, key, replacementMap, fallback)
        : undefined
    },
    [i18nMap],
  )

  const setLocaleData = React.useCallback(
    (languageCode: LanguageCodes) => {
      setPreferredLanguage(languageCode)

      // This should never be passed as a prop outside of a testing context.
      // We need this to be passed as a prop within tests due to the async nature of webpack dynamic imports
      if (i18nMap) {
        return
      }

      setLocale(languageCode).then(() => {
        getI18nMap(languageCode).then((map) => {
          seti18nMap(map.default)
        })
      })
    },
    [i18nMap],
  )

  React.useEffect(() => {
    setLocaleData(languageCode || LanguageCodes.ENGLISH)
  }, [languageCode, setLocaleData])

  return i18nMap ? (
    <I18nContext.Provider
      value={{
        languageCode,
        t: i18nTranslate,
      }}
    >
      {children}
    </I18nContext.Provider>
  ) : null
}

export const useI18n = () => {
  return React.useContext(I18nContext)
}
